// Sidebar Navigation Component - Persistent left sidebar for authenticated pages
import { createElement } from '@/utils/dom'
import { authService, type User } from '@/services/auth'
import { router } from '@/utils/router'

// Latest novidades version - update this when adding new release notes
const LATEST_NOVIDADES_VERSION = '2026-01-28'
const NOVIDADES_SEEN_KEY = 'novidades_last_seen_version'

export interface SidebarNavigationProps {
  currentPage: string
  onNavigate?: (page: string) => void
}

export class SidebarNavigation {
  private currentPage: string
  private user: User | null = null
  private onNavigate?: (page: string) => void
  private hasNewNovidades: boolean = false

  constructor(props: SidebarNavigationProps) {
    this.currentPage = props.currentPage
    this.onNavigate = props.onNavigate
    this.user = authService.getUser()
    this.hasNewNovidades = this.checkForNewNovidades()
  }

  private checkForNewNovidades(): boolean {
    const lastSeenVersion = localStorage.getItem(NOVIDADES_SEEN_KEY)
    return lastSeenVersion !== LATEST_NOVIDADES_VERSION
  }

  private markNovidadesAsSeen(): void {
    localStorage.setItem(NOVIDADES_SEEN_KEY, LATEST_NOVIDADES_VERSION)
    this.hasNewNovidades = false

    // Remove animation class from the icon
    const novidadesIcon = document.querySelector('[data-page="whats-new"] .nav-icon')
    novidadesIcon?.classList.remove('nav-icon-pulse')
  }

  render(): HTMLElement {
    const sidebar = createElement('aside', { className: 'sidebar' })

    sidebar.innerHTML = `
      <div class="sidebar-content">
        <!-- Logo/Brand -->
        <div class="sidebar-header">
          <div class="sidebar-logo">
            <span class="logo-icon">🗺️</span>
            <span class="logo-text">Mapas KM</span>
          </div>
        </div>

        <!-- Navigation Menu -->
        <nav class="sidebar-nav">
          <ul class="nav-list">
            <li class="nav-item">
              <button 
                class="nav-link ${this.currentPage === 'dashboard' ? 'active' : ''}" 
                data-page="dashboard"
              >
                <i class="fa-solid fa-home nav-icon"></i>
                <span class="nav-text">Dashboard</span>
              </button>
            </li>
            
            <li class="nav-item">
              <button 
                class="nav-link ${this.currentPage === 'generate' ? 'active' : ''}" 
                data-page="generate"
                data-action="generate-route"
              >
                <i class="fa-solid fa-route nav-icon"></i>
                <span class="nav-text">Gerar Rota</span>
              </button>
            </li>
            
            <li class="nav-item">
              <button 
                class="nav-link ${this.currentPage === 'trip-purposes' ? 'active' : ''}" 
                data-page="trip-purposes"
              >
                <i class="fa-solid fa-list-check nav-icon"></i>
                <span class="nav-text">Motivos de Viagem</span>
              </button>
            </li>
            
            <li class="nav-item">
              <button
                class="nav-link ${this.currentPage === 'whats-new' ? 'active' : ''}"
                data-page="whats-new"
              >
                <i class="fa-solid fa-gift nav-icon ${this.hasNewNovidades ? 'nav-icon-pulse' : ''}"></i>
                <span class="nav-text">Novidades</span>
              </button>
            </li>
            
            ${this.user?.role === 'admin' ? `
            <li class="nav-item nav-item-admin">
              <button 
                class="nav-link nav-link-admin ${this.currentPage === 'admin' ? 'active' : ''}" 
                data-page="admin"
              >
                <i class="fa-solid fa-shield-halved nav-icon"></i>
                <span class="nav-text">Administração</span>
              </button>
            </li>
            ` : ''}
          </ul>
        </nav>

        <!-- Company Details Card -->
        <div class="sidebar-footer">
          ${this.renderCompanyCard()}
          
          <!-- Settings Button -->
          <button class="btn btn-outline btn-sm settings-btn ${this.currentPage === 'settings' ? 'active' : ''}" id="sidebar-settings" data-page="settings">
            <i class="fa-solid fa-cog"></i>
            <span>Configurar</span>
          </button>
          
          <!-- Logout Button -->
          <button class="btn btn-outline btn-sm logout-btn" id="sidebar-logout">
            <i class="fa-solid fa-sign-out-alt"></i>
            <span>Terminar Sessão</span>
          </button>
        </div>
      </div>
    `

    this.addEventListeners(sidebar)
    return sidebar
  }

  private renderCompanyCard(): string {
    if (!this.user) {
      return `
        <div class="company-card">
          <div class="company-info">
            <p class="company-name">Utilizador Anónimo</p>
            <p class="company-details">Sem dados disponíveis</p>
          </div>
        </div>
      `
    }

    const companyName = this.user.company_name || 'Empresa Individual'
    const userName = this.user.name || 'Utilizador'
    const userNIF = this.user.nif || 'N/A'
    const userEmail = this.user.email || 'N/A'

    return `
      <div class="company-card">
        <div class="company-avatar">
          <i class="fa-solid fa-building"></i>
        </div>
        <div class="company-info">
          <p class="company-name">${this.escapeHtml(companyName)}</p>
          <p class="company-details">
            <strong>NIF:</strong> ${this.escapeHtml(userNIF)}<br>
            <strong>Nome:</strong> ${this.escapeHtml(userName)}
            ${userEmail !== 'N/A' ? `<br><strong>Email:</strong> ${this.escapeHtml(userEmail)}` : ''}
          </p>
        </div>
      </div>
    `
  }

  private addEventListeners(sidebar: HTMLElement): void {
    // Navigation links
    const navLinks = sidebar.querySelectorAll('.nav-link')
    navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        const button = e.currentTarget as HTMLButtonElement
        const page = button.getAttribute('data-page')
        const action = button.getAttribute('data-action')

        if (action === 'generate-route') {
          // Special action: trigger route generation modal
          this.triggerRouteGeneration()
        } else if (page) {
          this.handleNavigation(page)
        }
      })
    })

    // Settings button in footer
    const settingsBtn = sidebar.querySelector('#sidebar-settings')
    settingsBtn?.addEventListener('click', (e) => {
      e.preventDefault()
      this.handleNavigation('settings')
    })

    // Logout button
    const logoutBtn = sidebar.querySelector('#sidebar-logout')
    logoutBtn?.addEventListener('click', async () => {
      await this.handleLogout()
    })

    // Responsive collapse toggle (for mobile)
    this.addResponsiveHandlers(sidebar)
  }

  private triggerRouteGeneration(): void {
    // Dispatch custom event that the dashboard can listen to
    const event = new CustomEvent('sidebar:generate-route')
    document.dispatchEvent(event)

    // Also call onNavigate if provided
    if (this.onNavigate) {
      this.onNavigate('generate')
    }
  }

  private handleNavigation(page: string): void {
    // Update active state immediately for better UX
    this.updateActiveState(page)

    // Mark novidades as seen when navigating to whats-new
    if (page === 'whats-new') {
      this.markNovidadesAsSeen()
    }

    // Call navigation callback if provided
    if (this.onNavigate) {
      this.onNavigate(page)
    } else {
      // Default navigation behavior
      this.navigateToPage(page)
    }
  }

  private navigateToPage(page: string): void {
    switch (page) {
      case 'dashboard':
        router.navigate('/dashboard')
        break
      case 'statistics':
        router.navigate('/statistics')
        break
      case 'settings':
        router.navigate('/settings')
        break
      case 'trip-purposes':
        router.navigate('/trip-purposes')
        break
      case 'whats-new':
        router.navigate('/whats-new')
        break
      case 'admin':
        router.navigate('/admin')
        break
      default:
        router.navigate('/dashboard')
    }
  }

  private updateActiveState(page: string): void {
    const sidebar = document.querySelector('.sidebar')
    if (!sidebar) return

    // Remove all active states
    sidebar.querySelectorAll('.nav-link.active').forEach(link => {
      link.classList.remove('active')
    })

    // Add active state to current page
    const currentLink = sidebar.querySelector(`[data-page="${page}"]`)
    if (currentLink) {
      currentLink.classList.add('active')
    }

    this.currentPage = page
  }

  private async handleLogout(): Promise<void> {
    try {
      await authService.logout()
      router.navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
      // Force navigation even if logout API fails
      router.navigate('/')
    }
  }

  private addResponsiveHandlers(sidebar: HTMLElement): void {
    // Add mobile menu toggle functionality
    const mediaQuery = window.matchMedia('(max-width: 768px)')

    const handleMobileLayout = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        // Mobile layout
        sidebar.classList.add('sidebar-mobile')
        this.addMobileToggle()
      } else {
        // Desktop layout
        sidebar.classList.remove('sidebar-mobile', 'sidebar-collapsed')
        this.removeMobileToggle()
      }
    }

    // Initial check
    handleMobileLayout(mediaQuery)

    // Listen for changes
    mediaQuery.addEventListener('change', handleMobileLayout)
  }

  private addMobileToggle(): void {
    // Only add toggle if it doesn't already exist
    if (document.querySelector('.sidebar-toggle')) return

    const toggle = createElement('button', {
      className: 'sidebar-toggle',
      attributes: { 'aria-label': 'Toggle sidebar' }
    })

    toggle.innerHTML = '<i class="fa-solid fa-bars"></i>'

    toggle.addEventListener('click', () => {
      const sidebar = document.querySelector('.sidebar')
      sidebar?.classList.toggle('sidebar-open')
    })

    // Close sidebar when clicking outside (mobile only)
    document.addEventListener('click', (e) => {
      const sidebar = document.querySelector('.sidebar')
      const isOpen = sidebar?.classList.contains('sidebar-open')

      if (isOpen && !sidebar?.contains(e.target as Node) && e.target !== toggle) {
        sidebar?.classList.remove('sidebar-open')
      }
    })

    // Add to page (could be in header or fixed position)
    document.body.appendChild(toggle)
  }

  private removeMobileToggle(): void {
    const toggle = document.querySelector('.sidebar-toggle')
    toggle?.remove()
  }

  private escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }

  // Public method to update the current page
  setCurrentPage(page: string): void {
    this.updateActiveState(page)
  }

  // Public method to refresh user data
  refreshUserData(): void {
    this.user = authService.getUser()
    const companyCard = document.querySelector('.company-card')
    if (companyCard) {
      companyCard.outerHTML = this.renderCompanyCard()
    }
  }
}

// Utility function to create sidebar with default props
export function createSidebar(currentPage: string, onNavigate?: (page: string) => void): HTMLElement {
  const sidebar = new SidebarNavigation({ currentPage, onNavigate })
  return sidebar.render()
}
